import React, { useEffect, useMemo, useState } from "react"
import axios from "axios"
import moment from "moment-timezone"
import { saveAs } from "file-saver"
import {
  Download,
  Users,
  ChevronLeft,
  ChevronRight,
  Eye,
  ClipboardList,
  Info,
  UserPlus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
} from "lucide-react"
import CompanySelector from "ATSComponents/Common/CompanySelector"
import emptyReportImage from "assets/images/tracker-empty.svg"
import "./TrackerReport.css"
import {
  documentationCandidates,
  documentTasks,
  checklistNames,
  taskFields,
} from "./trackerDocumentation"

const endpoint = "/api/v1/tracker-report/"
const interviewTime = event => {
  if (!event.event_time) return "--"
  if (!event.time_zone || !moment.tz.zone(event.time_zone)) {
    if (!/(?:Z|[+-]\d{2}:?\d{2})$/i.test(event.event_time)) return "--"
    const timestamp = moment.parseZone(event.event_time)
    return timestamp.isValid() ? timestamp.format("DD-MM-YYYY hh:mm A") : "--"
  }
  const value = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(event.event_time)
    ? moment.parseZone(event.event_time).tz(event.time_zone)
    : moment.tz(event.event_time, event.time_zone)
  return value.isValid() ? value.format("DD-MM-YYYY hh:mm A") : "--"
}
const interviewFields = [
  ["Job Title", "job_title"],
  ["Pipeline Stage", "candidate_pipeline_stage"],
  ["Event Type", "event_type"],
  ["Event Subtype", "event_sub_type"],
  ["Subject", "subject"],
]
const show = value =>
  value === null || value === undefined || value === "" ? "--" : String(value)
const dash = value =>
  value === null || value === undefined || String(value).trim() === ""
    ? "—"
    : String(value)
const localDate = date =>
  date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(date.getDate()).padStart(2, "0")}`
    : undefined
const dateText = value => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/)
  return match ? `${match[3]}-${match[2]}-${match[1]}` : dash(value)
}
const candidateFields = [
  ["Email", "email", "hrms_email"],
  ["Phone", "phone", "mobile_phone"],
  ["Current Salary", "current_salary"],
  ["Annual CTC", "annual_ctc"],
  ["Expected Salary", "expected_salary"],
  ["Notice Period", "notice_period"],
  ["Total Experience", "total_experience"],
  ["Current Company", "current_company"],
  ["Work Type", "workplace_name"],
  ["Shift Timing", "shift_timing"],
  ["Designation", "designation"],
  ["Reporting Manager", "reporting_manager"],
  ["Reporting Manager Email", "reporting_manager_email"],
  ["Location", "location"],
  ["Stage", "stage"],
  ["Status", "status"],
  ["Offer Letter Status", "offer_letter_status", "offer_status"],
  ["Joining Status", "joining_status"],
  ["Acknowledged At", "acknowledged_at"],
  ["Rejected At", "rejected_at", "rejected_date"],
  ["Joined At", "joined_at"],
  ["Rejected By", "rejected_by"],
  [
    "Rejection Comments",
    "employee_rejection_comments",
    "hr_rejection_comments",
  ],
  ["Joining Date", "date_of_joining", "joined_at"],
  ["Applied Date", "applied_date", "applied_on"],
]
const fieldValue = (candidate, keys) =>
  keys
    .map(key => candidate[key])
    .find(value => value !== undefined && value !== null && value !== "")
const isIdField = key => /(?:^id$|ids?$)/i.test(key.replace(/_/g, ""))
const withoutIds = value =>
  Array.isArray(value)
    ? value.map(withoutIds)
    : value && typeof value === "object"
    ? Object.fromEntries(
        Object.entries(value)
          .filter(([key]) => !isIdField(key))
          .map(([key, item]) => [key, withoutIds(item)])
      )
    : value
const detailText = value =>
  typeof value === "object" && value !== null
    ? JSON.stringify(withoutIds(value))
    : show(value)
const excelCandidateFields = candidateFields.filter(
  ([label]) => label !== "Rejected At"
)
excelCandidateFields.splice(
  excelCandidateFields.findIndex(([label]) => label === "Joining Date"),
  0,
  ["Status History", "status_history"]
)
const readableExcelText = value => {
  if (Array.isArray(value))
    return value.length ? value.map(readableExcelText).join("\n\n") : "--"
  if (value && typeof value === "object")
    return Object.entries(value)
      .filter(([key]) => !isIdField(key))
      .map(
        ([key, item]) => `${key.replace(/_/g, " ")}: ${readableExcelText(item)}`
      )
      .join("\n")
  return show(value)
}
const renderCandidateValue = (label, value) => {
  if (
    [
      "Annual CTC",
      "Work Type",
      "Shift Timing",
      "Reporting Manager",
      "Reporting Manager Email",
    ].includes(label)
  )
    return dash(value)
  if (label.includes("Rejection Comments") && Array.isArray(value)) {
    return value.length ? (
      <div className="tracker-rejection-list">
        {value.map((item, index) =>
          item && typeof item === "object" ? (
            <dl key={index}>
              {[
                ["Template", item?.template_name ?? item?.template?.name],
                ["Rejection Status", item?.rejection_status ?? item?.status],
                ["Comments", item?.rejection_comments ?? item?.comments],
                ["Rejected Date", item?.rejected_date ?? item?.rejected_at],
              ].map(([title, text]) => (
                <div key={title}>
                  <dt>{title}</dt>
                  <dd>{detailText(text)}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <div key={index}>{dash(item)}</div>
          )
        )}
      </div>
    ) : (
      "—"
    )
  }
  if (label.includes("Rejection Comments")) return dash(value)
  return label.includes("Date") ? dateText(value) : detailText(value)
}
const candidateName = c => c?.candidate_name || "—"
const AddedByDetails = ({ candidate }) => (
  <section className="tracker-added-by">
    <h4>Added By</h4>
    <dl>
      <div>
        <dt>Added By</dt>
        <dd>{candidate?.added_by_name || "—"}</dd>
      </div>
      <div>
        <dt>Email</dt>
        <dd>{candidate?.added_by_email || "—"}</dd>
      </div>
    </dl>
  </section>
)
const CandidateHeading = ({ candidate }) => {
  const name = candidateName(candidate)
  const initials =
    name === "—"
      ? "—"
      : name
          .trim()
          .split(/\s+/)
          .slice(0, 2)
          .map(part => part[0])
          .join("")
          .toUpperCase()
  const palette = [
    ["#e3f8f4", "#197967", "#a7dfd2"],
    ["#fcebf3", "#aa4777", "#efbbd3"],
    ["#edf0ff", "#5968ab", "#c8d0f3"],
    ["#fff5dd", "#8e6b23", "#ecd9a5"],
    ["#e6f4fc", "#36779b", "#b6dced"],
  ]
  const hash = Array.from(name).reduce(
    (value, char) => (value * 31 + char.charCodeAt(0)) >>> 0,
    0
  )
  const [background, color, borderColor] = palette[hash % palette.length]
  return (
    <h3 className="tracker-candidate-heading">
      <span className="tracker-candidate-name-label">Candidate Name:</span>
      <span
        className="tracker-candidate-avatar"
        style={{ background, color, borderColor }}
        aria-hidden="true"
      >
        {initials}
      </span>
      <span>{name}</span>
    </h3>
  )
}
const hideRejectionFields = candidate =>
  String(candidate.joining_status ?? candidate.JoiningStatus ?? "")
    .trim()
    .toLowerCase() === "joined" &&
  String(
    candidate.offer_letter_status ??
      candidate.OfferLetterStatus ??
      candidate.offer_status ??
      ""
  )
    .trim()
    .toLowerCase() === "acknowledged"
const isRejectionField = key =>
  [
    "rejectedat",
    "rejecteddate",
    "rejectedby",
    "rejectioncomments",
    "employeerejectioncomments",
    "hrrejectioncomments",
  ].includes(key.replace(/[^a-z]/gi, "").toLowerCase())
const candidatesOf = row =>
  Array.isArray(row.candidate_details) ? row.candidate_details : []
const summary = row => [
  show(row.entity || row.company_name),
  show(row.requirements || row.requirement || row.job_title),
  row.positions ?? 0,
  row.documentation ?? 0,
  row.offered_yti ?? row.yti ?? row.ytj ?? row.offered ?? 0,
  row.joined ?? 0,
  row.dropped ?? 0,
  row.yet_to_fill ?? row.open_positions ?? 0,
  row.interviews_scheduled ?? 0,
]
const headers = [
  "S.No",
  "Company",
  "Requirements",
  "Positions",
  "Documentation",
  "Offered/YTO",
  "Joined",
  "Dropped",
  "Yet to Fill",
  "Interviews Scheduled",
]
const reportColumnGuide = [
  ["S.No", "Sequential row number in this export."],
  ["Company", "Company or entity associated with the job order."],
  ["Requirements", "Job title or requirements configured for the job order."],
  ["Positions", "Total openings configured in the job order."],
  [
    "Documentation",
    "Candidates whose joining status is not Joined and who are not dropped or rejected.",
  ],
  [
    "Offered/YTO",
    "Candidates whose HRMS offerLetterStatus is Acknowledged and joiningStatus is not Joined.",
  ],
  [
    "Joined",
    "Candidates whose HRMS offerLetterStatus is Acknowledged and joiningStatus is Joined.",
  ],
  [
    "Dropped",
    "Candidates with joining status Dropped, Rejected, or Not Joined, or employee/HR rejection details.",
  ],
  ["Yet to Fill", "Positions minus Joined, never below zero."],
  ["Interviews Scheduled", "Unique interview events associated with the job."],
]
const parseReport = data => {
  const rows = data?.results?.data ?? data?.results ?? data?.data
  if (!Array.isArray(rows))
    throw new Error("The API returned an unexpected report format.")
  return { rows, count: Number(data.count ?? rows.length) }
}
// Page size used to stream the "All" view: small enough that the first rows
// appear quickly, large enough to keep the number of requests low.
const ALL_ROWS_CHUNK_SIZE = 25
// Remaining "All" pages are fetched in parallel instead of one after the
// other, so total load time is not the sum of every request.
const PARALLEL_PAGE_REQUESTS = 4
const EXPORT_PAGE_SIZE = 100
const SHIMMER_ROW_COUNT = 8
// Short-lived cache so paging back/forth or re-selecting a filter is instant.
const REPORT_CACHE_TTL_MS = 60 * 1000
const REPORT_CACHE_MAX_ENTRIES = 50
const reportCache = new Map()
const fetchReportPage = async (params, page, size, signal) => {
  const key = `${JSON.stringify(params)}|${page}|${size}`
  const cached = reportCache.get(key)
  if (cached && Date.now() - cached.at < REPORT_CACHE_TTL_MS)
    return cached.value
  const { data } = await axios.get(endpoint, {
    params: { ...params, page, size },
    signal,
  })
  const value = parseReport(data)
  reportCache.delete(key)
  reportCache.set(key, { at: Date.now(), value })
  if (reportCache.size > REPORT_CACHE_MAX_ENTRIES)
    reportCache.delete(reportCache.keys().next().value)
  return value
}
const errorMessage = error => {
  const data = error.response?.data
  if (typeof data === "object" && data) return JSON.stringify(data)
  if (typeof data === "string" && !data.includes("<html")) return data
  return error.response?.status
    ? `Unable to load Recruitment Updates (HTTP ${error.response.status}).`
    : error.message || "Unable to load report."
}

// Loads every page of the report. Page 1 decides the total; the rest are
// requested concurrently. onProgress receives the rows loaded so far, always
// in page order (rows never jump around while loading).
const fetchAllReportPages = async (
  params,
  pageSize,
  { signal, onFirstPage, onProgress } = {}
) => {
  const first = await fetchReportPage(params, 1, pageSize, signal)
  onFirstPage?.(first)
  const totalPages = Math.ceil(first.count / pageSize)
  const pages = [first.rows]
  let nextPage = 2
  let contiguous = 1
  const worker = async () => {
    while (nextPage <= totalPages) {
      const pageNumber = nextPage
      nextPage += 1
      const result = await fetchReportPage(params, pageNumber, pageSize, signal)
      if (signal?.aborted) return
      pages[pageNumber - 1] = result.rows
      let advanced = false
      while (contiguous < totalPages && pages[contiguous]) {
        contiguous += 1
        advanced = true
      }
      if (advanced)
        onProgress?.({
          rows: pages.slice(0, contiguous).flat(),
          count: first.count,
        })
    }
  }
  await Promise.all(
    Array.from(
      { length: Math.min(PARALLEL_PAGE_REQUESTS, Math.max(totalPages - 1, 0)) },
      worker
    )
  )
  return { rows: pages.flat(), count: first.count }
}
const ShimmerRows = ({ count, label }) => (
  <>
    {Array.from({ length: count }, (_, rowIndex) => (
      <tr
        key={`shimmer-${rowIndex}`}
        className="tracker-shimmer-row"
        aria-hidden={rowIndex > 0 ? "true" : undefined}
      >
        {headers.map((header, column) => (
          <td key={header}>
            {rowIndex === 0 && column === 0 && (
              <span className="tracker-sr-only" role="status">
                {label}
              </span>
            )}
            <span
              className={`tracker-shimmer-bar${
                column === 1 || column === 2 ? " is-wide" : ""
              }`}
            />
          </td>
        ))}
      </tr>
    ))}
  </>
)

export default function TrackerReport() {
  const [companyId, setCompanyId] = useState(null)
  const [companyName, setCompanyName] = useState("")
  const [showCalculationRules, setShowCalculationRules] = useState(false)
  const [filters, setFilters] = useState({
    jobs: [],
    recruiters: [],
    from: null,
    to: null,
  })
  const [page, setPage] = useState(1),
    [size, setSize] = useState("all")
  const rowOffset = size === "all" ? 0 : (page - 1) * size
  const [report, setReport] = useState({ rows: [], count: 0 })
  const [loading, setLoading] = useState(false),
    [exporting, setExporting] = useState(false),
    [error, setError] = useState("")
  const [detailRow, setDetailRow] = useState(null)
  const [detailColumn, setDetailColumn] = useState(2)
  const [resumePreviewUrl, setResumePreviewUrl] = useState("")
  const [resumePreviewCandidateKey, setResumePreviewCandidateKey] = useState("")
  const [sort, setSort] = useState({ column: 0, direction: "asc" })
  const sortedRows = useMemo(() => {
    const values = report.rows.map((row, index) => ({
      row,
      serial: rowOffset + index + 1,
    }))
    return values.sort((a, b) => {
      const left =
        sort.column === 0 ? a.serial : summary(a.row)[sort.column - 1]
      const right =
        sort.column === 0 ? b.serial : summary(b.row)[sort.column - 1]
      const comparison =
        sort.column === 0 || (sort.column >= 3 && sort.column <= 9)
          ? (Number(left) || 0) - (Number(right) || 0)
          : String(left).localeCompare(String(right), undefined, {
              numeric: true,
              sensitivity: "base",
            })
      return sort.direction === "asc" ? comparison : -comparison
    })
  }, [report.rows, rowOffset, sort])
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailError, setDetailError] = useState("")
  // Background loading of the remaining pages when "All" rows are shown.
  const [loadingMore, setLoadingMore] = useState(false)
  const params = useMemo(
    () => ({
      company_id: companyId,
      // Full applicant list is only needed for the Excel export.
      include_candidate_details: false,
      ...(filters.jobs.length ? { job_id: filters.jobs.join(",") } : {}),
      start_date: localDate(filters.from),
      end_date: localDate(filters.to),
    }),
    [companyId, filters]
  )
  const paramsKey = JSON.stringify(params)
  // Old rows remain visible (dimmed) while a new page/filter is loading.
  const isRefreshing = loading && report.rows.length > 0
  useEffect(() => {
    document.title = "ATS - Recruitment Updates"
  }, [])
  useEffect(() => {
    setDetailsOpen(false)
    setDetailRow(null)
    setDetailError("")
    setLoadingMore(false)
    if (!companyId) {
      setReport({ rows: [], count: 0 })
      setLoading(false)
      return
    }
    // Abort in-flight requests when filters/page change so a slow, stale
    // response can never overwrite newer data.
    const controller = new AbortController()
    const { signal } = controller
    // Previous rows stay on screen (dimmed) while the new data loads, so
    // the table never goes blank or freezes between filter changes.
    setLoading(true)
    setError("")
    let firstPageShown = false
    const run = async () => {
      if (size !== "all") {
        const result = await fetchReportPage(params, page, size, signal)
        if (signal.aborted) return
        firstPageShown = true
        setReport(result)
        setLoading(false)
        return
      }
      // "All": render the first chunk immediately, then load the remaining
      // pages in parallel and append them (in order) as they arrive.
      await fetchAllReportPages(params, ALL_ROWS_CHUNK_SIZE, {
        signal,
        onFirstPage: first => {
          if (signal.aborted) return
          firstPageShown = true
          setReport(first)
          setLoading(false)
          setLoadingMore(first.count > first.rows.length)
        },
        onProgress: partial => {
          if (!signal.aborted) setReport(partial)
        },
      })
      if (!signal.aborted) setLoadingMore(false)
    }
    run().catch(failure => {
      if (signal.aborted || axios.isCancel(failure)) return
      controller.abort() // stop the other parallel page requests
      setError(errorMessage(failure))
      // Don't leave rows from the previous filters on screen as if they
      // matched the new ones; keep already-streamed rows of this request.
      if (!firstPageShown) setReport({ rows: [], count: 0 })
      setLoading(false)
      setLoadingMore(false)
    })
    return () => controller.abort()
  }, [paramsKey, page, size])
  const changeFilter = (key, value) => {
    setPage(1)
    setFilters(previous => ({ ...previous, [key]: value }))
  }
  const openDetails = (event, row, column) => {
    event?.preventDefault?.()
    setDetailError("")
    setResumePreviewUrl("")
    setResumePreviewCandidateKey("")
    setDetailRow(row)
    setDetailColumn(column)
    setDetailsOpen(true)
  }
  const detailCandidates =
    detailColumn === 4
      ? detailRow?.offered_details?.candidates || []
      : detailRow?.joined_details?.candidates || []
  const droppedCandidates = Array.isArray(
    detailRow?.dropped_details?.candidates
  )
    ? detailRow.dropped_details.candidates
    : []
  const visibleCandidates = Array.isArray(detailCandidates)
    ? detailCandidates
    : []
  const interviewEvents = Array.isArray(detailRow?.interview_details)
    ? detailRow.interview_details
    : []
  const exportExcel = async () => {
    setExporting(true)
    setError("")
    try {
      // Export needs the full applicant list for the "Candidate Details"
      // sheet, which the on-screen report does not load.
      const exportParams = { ...params, include_candidate_details: true }
      const exported = await fetchAllReportPages(exportParams, EXPORT_PAGE_SIZE)
      if (exported.rows.length < exported.count)
        throw new Error("Export stopped before all report rows were returned.")
      const all = exported.rows
      const { Workbook } = await import("exceljs")
      const workbook = new Workbook()
      const styleRow = (row, color, header = false) => {
        row.height = header ? 32 : 28
        row.eachCell({ includeEmpty: true }, cell => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: color },
          }
          cell.font = {
            name: "Calibri",
            size: 11,
            bold: header,
            color: { argb: header ? "FFFFFFFF" : "FF17212B" },
          }
          cell.alignment = {
            vertical: "middle",
            horizontal: header ? "center" : "left",
            wrapText: true,
          }
          const edge = { style: "thin", color: { argb: "FF263342" } }
          cell.border = { top: edge, bottom: edge, left: edge, right: edge }
        })
      }
      const guideSheet = workbook.addWorksheet("Report Guide", {
        views: [{ state: "frozen", ySplit: 4 }],
      })
      guideSheet.columns = [{ width: 28 }, { width: 100 }]
      const guideTitle = guideSheet.addRow(["Recruitment Updates", ""])
      guideTitle.height = 32
      guideSheet.mergeCells(guideTitle.number, 1, guideTitle.number, 2)
      styleRow(guideTitle, "FF062158", true)
      guideTitle.getCell(1).alignment = {
        vertical: "middle",
        horizontal: "left",
      }
      const guideDescription = guideSheet.addRow([
        "Purpose",
        "This workbook contains the complete tracker summary, candidate details, and documentation task data for the selected company and filters.",
      ])
      styleRow(guideDescription, "FFFFFFFF")
      const guideHeader = guideSheet.addRow([
        "Column",
        "Meaning and calculation rule",
      ])
      styleRow(guideHeader, "FF17617A", true)
      reportColumnGuide.forEach(([column, meaning]) => {
        const row = guideSheet.addRow([column, meaning])
        styleRow(row, "FFFFFFFF")
        row.height = 34
      })
      guideSheet.autoFilter = {
        from: { row: guideHeader.number, column: 1 },
        to: { row: guideSheet.rowCount, column: 2 },
      }
      const sheet = workbook.addWorksheet("Summary", {
        views: [{ state: "frozen", ySplit: companyName ? 2 : 1 }],
      })
      sheet.columns = headers.map((header, index) => ({
        width: [9, 22, 48, 14, 17, 14, 16, 25, 28, 48][index],
      }))
      for (let column = 4; column <= 10; column += 1) {
        sheet.getColumn(column).alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        }
      }
      if (companyName) {
        const companyRow = sheet.addRow([
          companyName,
          ...Array(headers.length - 1).fill(""),
        ])
        styleRow(companyRow, "FFFFFFFF")
        sheet.mergeCells(
          companyRow.number,
          1,
          companyRow.number,
          headers.length
        )
        companyRow.height = 36
        companyRow.getCell(1).font = {
          name: "Calibri",
          size: 12,
          bold: true,
          color: { argb: "FF173754" },
        }
        companyRow.getCell(1).alignment = {
          vertical: "middle",
          horizontal: "left",
          indent: 1,
        }
      }
      styleRow(sheet.addRow(headers), "FF062158", true)
      all.forEach((row, index) => {
        const dataRow = sheet.addRow([index + 1, ...summary(row)])
        styleRow(dataRow, "FFFFFFFF")
        dataRow.height = 44
        dataRow.eachCell((cell, column) => {
          if (index % 2 === 0 && column <= 8)
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF7CFB2" },
            }
          if (column >= 4 && column <= 10)
            cell.alignment = { ...cell.alignment, horizontal: "center" }
        })
      })
      sheet.autoFilter = {
        from: { row: companyName ? 2 : 1, column: 1 },
        to: { row: sheet.rowCount, column: headers.length },
      }
      const details = all.flatMap((row, index) =>
        candidatesOf(row).map(candidate => [
          index + 1,
          ...summary(row).slice(0, 2),
          candidateName(candidate),
          ...excelCandidateFields.map(([label, ...keys]) =>
            label.includes("Date")
              ? dateText(fieldValue(candidate, keys))
              : readableExcelText(fieldValue(candidate, keys))
          ),
          show(candidate.resume || candidate.resume_url),
        ])
      )
      const detailSheet = workbook.addWorksheet("Candidate Details", {
        views: [{ state: "frozen", ySplit: 1 }],
      })
      const detailHeaders = [
        "S.No",
        "Entity",
        "Requirements",
        "Candidate Name",
        ...excelCandidateFields.map(([label]) => label),
        "Resume Link",
      ]
      detailSheet.columns = detailHeaders.map(label => ({
        width:
          label === "S.No"
            ? 9
            : label === "Requirements" || label === "Resume Link"
            ? 45
            : 25,
      }))
      styleRow(detailSheet.addRow(detailHeaders), "FF062158", true)
      details.forEach((values, index) => {
        const row = detailSheet.addRow(values)
        styleRow(row, index % 2 === 0 ? "FFF7CFB2" : "FFFFFFFF")
        row.height = Math.max(
          32,
          ...values.map(
            value =>
              String(value)
                .split("\n")
                .reduce(
                  (lines, line) =>
                    lines + Math.max(1, Math.ceil(line.length / 25)),
                  0
                ) *
                15 +
              12
          )
        )
      })
      detailSheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: {
          row: Math.max(1, detailSheet.rowCount),
          column: detailHeaders.length,
        },
      }
      const documentationSheet = workbook.addWorksheet("Documentation Tasks", {
        views: [{ state: "frozen", ySplit: 1 }],
      })
      const documentationHeaders = [
        "Entity",
        "Requirements",
        "Candidate Name",
        "Email",
        ...taskFields,
        "Checklist Item Names",
      ]
      documentationSheet.columns = documentationHeaders.map(() => ({
        width: 28,
      }))
      styleRow(
        documentationSheet.addRow(documentationHeaders),
        "FF062158",
        true
      )
      all.forEach(row =>
        documentationCandidates(row).forEach(candidate => {
          const tasks = documentTasks(candidate)
          ;(tasks.length ? tasks : [null]).forEach(task => {
            const values = [
              ...summary(row).slice(0, 2),
              candidateName(candidate),
              show(candidate.email ?? candidate.hrms_email),
              ...taskFields.map(key => (task ? show(task[key]) : "--")),
              task ? checklistNames(task).join("\n") || "--" : "--",
            ]
            const entry = documentationSheet.addRow(values)
            styleRow(entry, "FFFFFFFF")
            entry.height = Math.max(
              32,
              ...values.map(value => String(value).split("\n").length * 15 + 12)
            )
          })
        })
      )
      const buffer = await workbook.xlsx.writeBuffer()
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        "Tracker_Report.xlsx"
      )
    } catch (failure) {
      setError(errorMessage(failure))
    } finally {
      setExporting(false)
    }
  }
  return (
    <div className="page-content tracker-report-page">
      <header className="tracker-report-heading">
        <div className="tracker-report-title">
          <span className="tracker-report-title-icon">
            <ClipboardList size={22} aria-hidden="true" />
          </span>
          <h1>Recruitment Updates</h1>
          <span className="tracker-report-info-wrap">
            <button
              type="button"
              className="tracker-report-info"
              aria-label="Report calculation rules"
              aria-expanded={showCalculationRules}
              onClick={() => setShowCalculationRules(current => !current)}
            >
              <Info size={16} aria-hidden="true" />
            </button>
            <span
              className={`tracker-report-rules ${
                showCalculationRules ? "is-open" : ""
              }`}
              role="tooltip"
            >
              <strong>Report calculation rules</strong>
              <span>
                <b>Positions:</b> Total openings configured in the job order.
              </span>
              <span>
                <b>Documentation:</b> Candidates whose joining status is not{" "}
                <code>Joined</code> and who are not dropped or rejected.
              </span>
              <span>
                <b>Offered/YTO:</b> Candidates whose HRMS{" "}
                <code>offerLetterStatus</code> is <code>Acknowledged</code> and{" "}
                <code>joiningStatus</code> is not <code>Joined</code>.
              </span>
              <span>
                <b>Joined:</b> Candidates whose HRMS{" "}
                <code>offerLetterStatus</code> is <code>Acknowledged</code> and{" "}
                <code>joiningStatus</code> is <code>Joined</code>.
              </span>
              <span>
                <b>Dropped:</b> Candidates with joining status{" "}
                <code>Dropped</code>, <code>Rejected</code>, or{" "}
                <code>Not Joined</code>, or candidates with employee/HR
                rejection details.
              </span>
              <span>
                <b>Yet to Fill:</b> <code>Positions - Joined</code>, never below
                zero.
              </span>
              <span>
                <b>Interviews Scheduled:</b> Unique interview events associated
                with the job.
              </span>
            </span>
          </span>
        </div>
        <button
          className="tracker-report-export"
          onClick={exportExcel}
          disabled={!companyId || loading || exporting || !report.count}
        >
          <Download size={16} aria-hidden="true" />
          {exporting ? "Exporting..." : "Export Excel"}
        </button>
        <div className="tracker-report-filters">
          <label>
            Company
            <CompanySelector
              selectedCompanyId={companyId}
              showAllOption
              allOptionValue="all"
              notifyGlobalChange={false}
              disabled={exporting}
              onCompanyChange={company => {
                setCompanyId(company?.company_id ?? company?.id ?? null)
                setCompanyName(
                  company?.company_name || company?.company_domain || ""
                )
                setPage(1)
                setFilters({ jobs: [], recruiters: [], from: null, to: null })
              }}
            />
          </label>
        </div>
      </header>
      {error && (
        <div className="tracker-report-error" role="alert">
          {error}
        </div>
      )}
      {detailError && (
        <div className="tracker-report-error" role="alert">
          {detailError}
        </div>
      )}
      <div className="tracker-report-table-scroll">
        {(isRefreshing || loadingMore) && (
          <div className="tracker-report-progress" role="status">
            <span className="tracker-sr-only">
              {isRefreshing ? "Updating report..." : "Loading more rows..."}
            </span>
          </div>
        )}
        <table className="tracker-report-table">
          <thead>
            {companyName && (
              <tr className="tracker-company-caption">
                <th colSpan={headers.length}>{companyName}</th>
              </tr>
            )}
            <tr>
              {headers.map((header, column) => (
                <th
                  key={header}
                  aria-sort={
                    sort.column === column
                      ? sort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  {column === 0 ? (
                    header
                  ) : (
                    <button
                      className="tracker-sort-header"
                      disabled={!report.rows.length}
                      onClick={() =>
                        setSort(previous => ({
                          column,
                          direction:
                            previous.column === column &&
                            previous.direction === "asc"
                              ? "desc"
                              : "asc",
                        }))
                      }
                    >
                      {header}
                      {sort.column === column ? (
                        sort.direction === "asc" ? (
                          <ArrowUp size={13} />
                        ) : (
                          <ArrowDown size={13} />
                        )
                      ) : (
                        <ArrowUpDown size={13} />
                      )}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody
            className={isRefreshing ? "tracker-report-refreshing" : undefined}
            aria-busy={loading || loadingMore}
          >
            {loading && !report.rows.length ? (
              <ShimmerRows
                count={SHIMMER_ROW_COUNT}
                label="Loading report..."
              />
            ) : report.rows.length ? (
              sortedRows.map(({ row, serial }, index) => (
                <React.Fragment key={`${row.job_id || "row"}-${index}`}>
                  <tr className={index % 2 === 0 ? "tracker-peach-row" : ""}>
                    <td>{serial}</td>
                    {summary(row).map((value, column) => (
                      <td key={column}>
                        {column >= 2 &&
                        column <= 8 &&
                        (column !== 3 || Number(value) > 0) ? (
                          <button
                            className={`tracker-metric-trigger${
                              column === 6 ? " tracker-dropped-trigger" : ""
                            }`}
                            aria-label={`${
                              headers[column + 1]
                            }: ${value}, view details`}
                            onClick={e => openDetails(e, row, column)}
                          >
                            {value}
                          </button>
                        ) : (
                          value
                        )}
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={headers.length}>
                  <div className="tracker-report-empty">
                    <img src={emptyReportImage} alt="" />
                    <strong>
                      {companyId ? "No data found" : "Select a company"}
                    </strong>
                    <p>
                      {companyId
                        ? "No report records match the selected filters."
                        : "Choose a company to view its tracker report."}
                    </p>
                  </div>
                </td>
              </tr>
            )}
            {loadingMore && !loading && (
              <ShimmerRows
                count={Math.min(
                  3,
                  Math.max(report.count - report.rows.length, 1)
                )}
                label={`Loading more rows (${report.rows.length} of ${report.count})...`}
              />
            )}
          </tbody>
          {!loading && !loadingMore && report.rows.length > 0 && (
            <tfoot>
              <tr className="recruitment-totals-row">
                <td colSpan={2} className="recruitment-totals-empty" />
                <td className="recruitment-totals-empty" />
                {[2, 3, 4, 5, 6, 7, 8].map(column => (
                  <td key={column}>
                    {report.rows.reduce(
                      (total, row) =>
                        total + (Number(summary(row)[column]) || 0),
                      0
                    )}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <footer className="tracker-report-pagination">
        <span>
          {report.count
            ? size === "all"
              ? `1-${report.rows.length} of ${report.count}`
              : `${(page - 1) * size + 1}-${Math.min(
                  page * size,
                  report.count
                )} of ${report.count}`
            : "0 results"}
        </span>
        <label>
          Rows per page{" "}
          <select
            value={size}
            disabled={exporting}
            onChange={e => {
              setSize(e.target.value === "all" ? "all" : Number(e.target.value))
              setPage(1)
            }}
          >
            {[10, 20, 50].map(value => (
              <option key={value}>{value}</option>
            ))}
            <option value="all">All</option>
          </select>
        </label>
        <button
          title="Previous page"
          aria-label="Previous page"
          disabled={size === "all" || page === 1 || exporting}
          onClick={() => setPage(p => p - 1)}
        >
          <ChevronLeft size={18} />
        </button>
        <button
          title="Next page"
          aria-label="Next page"
          disabled={size === "all" || page * size >= report.count || exporting}
          onClick={() => setPage(p => p + 1)}
        >
          <ChevronRight size={18} />
        </button>
      </footer>
      {detailsOpen && (
        <div
          className="tracker-custom-modal-overlay"
          onMouseDown={event => {
            if (event.target === event.currentTarget) {
              setDetailsOpen(false)
              setResumePreviewUrl("")
              setResumePreviewCandidateKey("")
            }
          }}
        >
          <section
            className="tracker-candidate-panel tracker-details-dialog tracker-custom-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tracker-details-title"
          >
            <header className="tracker-custom-modal-header">
              <h1 id="tracker-details-title">Report Details</h1>
              <button
                type="button"
                className="tracker-custom-modal-close"
                aria-label="Close report details"
                onClick={() => {
                  setDetailsOpen(false)
                  setResumePreviewUrl("")
                  setResumePreviewCandidateKey("")
                }}
              >
                <X size={20} aria-hidden="true" />
              </button>
            </header>
            <div className="tracker-custom-modal-content">
              <h2>
                {headers[detailColumn + 1]}{" "}
                <span>
                  {show(
                    detailRow?.requirements ||
                      detailRow?.requirement ||
                      detailRow?.job_title
                  )}
                </span>
              </h2>
              {detailColumn !== 7 && (
                <div className="tracker-metric-summary">
                  {headers[detailColumn + 1]}:{" "}
                  <strong>
                    {detailRow ? summary(detailRow)[detailColumn] : 0}
                  </strong>
                </div>
              )}
              {detailColumn === 2 ? (
                <dl className="tracker-position-details">
                  <div>
                    <dt>Job Title</dt>
                    <dd>
                      {show(
                        detailRow?.position_details?.job_title ??
                          detailRow?.requirements ??
                          detailRow?.requirement ??
                          detailRow?.job_title
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Total Positions</dt>
                    <dd>
                      {detailRow?.position_details?.count ??
                        (detailRow ? summary(detailRow)[2] : 0)}
                    </dd>
                  </div>
                </dl>
              ) : detailColumn === 3 ? (
                <div className="tracker-candidate-scroll">
                  {documentationCandidates(detailRow).length ? (
                    documentationCandidates(detailRow).map(
                      (candidate, index) => (
                        <article key={index}>
                          <CandidateHeading candidate={candidate} />
                          <dl>
                            <div>
                              <dt>Email</dt>
                              <dd>
                                {show(candidate.email ?? candidate.hrms_email)}
                              </dd>
                            </div>
                          </dl>
                          {documentTasks(candidate).length ? (
                            documentTasks(candidate).map((task, taskIndex) => (
                              <section
                                className="tracker-feedback-entry"
                                key={taskIndex}
                              >
                                <dl>
                                  {taskFields.map(key => (
                                    <div key={key}>
                                      <dt>{key}</dt>
                                      <dd>{show(task[key])}</dd>
                                    </div>
                                  ))}
                                  <div className="tracker-comment-field">
                                    <dt>Checklist Items</dt>
                                    <dd>
                                      {checklistNames(task).length ? (
                                        <ul className="tracker-document-checklist">
                                          {checklistNames(task).map(
                                            (name, itemIndex) => (
                                              <li key={itemIndex}>
                                                {show(name)}
                                              </li>
                                            )
                                          )}
                                        </ul>
                                      ) : (
                                        "--"
                                      )}
                                    </dd>
                                  </div>
                                </dl>
                              </section>
                            ))
                          ) : (
                            <p>No document collection tasks available</p>
                          )}
                        </article>
                      )
                    )
                  ) : (
                    <p>No documentation candidate details available</p>
                  )}
                </div>
              ) : detailColumn === 6 ? (
                <div className="tracker-candidate-scroll">
                  {droppedCandidates.length ? (
                    droppedCandidates.map((candidate, index) => {
                      return (
                        <article
                          key={`${
                            candidate.job_candidate_id ||
                            candidate.candidate_id ||
                            index
                          }-${index}`}
                        >
                          <CandidateHeading candidate={candidate} />
                          <dl>
                            <div>
                              <dt>Email</dt>
                              <dd>
                                {dash(
                                  candidate.email ??
                                    candidate.hrms_email ??
                                    candidate.candidate_email
                                )}
                              </dd>
                            </div>
                            <div>
                              <dt>Job Candidate ID</dt>
                              <dd>
                                {dash(
                                  candidate.job_candidate_id ??
                                    candidate.candidate_id ??
                                    candidate.id
                                )}
                              </dd>
                            </div>
                            <div>
                              <dt>Joining Status</dt>
                              <dd>
                                {dash(
                                  candidate.joining_status ??
                                    candidate.joiningStatus
                                )}
                              </dd>
                            </div>
                            <div>
                              <dt>Offer Letter Status</dt>
                              <dd>
                                {dash(
                                  candidate.offer_letter_status ??
                                    candidate.offer_status ??
                                    candidate.offerLetterStatus
                                )}
                              </dd>
                            </div>
                            <div>
                              <dt>Rejected By</dt>
                              <dd>{dash(candidate.rejected_by)}</dd>
                            </div>
                            <div className="tracker-comment-field">
                              <dt>Employee Rejection Comments</dt>
                              <dd>
                                {dash(candidate.employee_rejection_comments)}
                              </dd>
                            </div>
                            <div className="tracker-comment-field">
                              <dt>HR Rejection Comments</dt>
                              <dd>{dash(candidate.hr_rejection_comments)}</dd>
                            </div>
                          </dl>
                        </article>
                      )
                    })
                  ) : (
                    <p>No dropped candidates.</p>
                  )}
                </div>
              ) : detailColumn === 8 ? (
                <div className="tracker-candidate-scroll">
                  {interviewEvents.length ? (
                    interviewEvents.map((event, index) => {
                      const feedback = event.panel_feedback || []
                      return (
                      <article
                        key={`${
                          event.calendar_event_id ?? event.calendar_id ?? index
                        }-${index}`}
                      >
                        <CandidateHeading candidate={event} />
                        <AddedByDetails candidate={event} />
                        <dl>
                          <div>
                            <dt>Interview Date and Time</dt>
                            <dd>{interviewTime(event)}</dd>
                          </div>
                          <div>
                            <dt>Candidate Email</dt>
                            <dd>{event.candidate_email || "—"}</dd>
                          </div>
                          {interviewFields.map(([label, ...keys]) => {
                            const value =
                              label === "Job Title"
                                ? event.job_title ??
                                  detailRow?.position_details?.job_title ??
                                  detailRow?.requirements ??
                                  detailRow?.requirement ??
                                  detailRow?.job_title
                                : fieldValue(event, keys)
                            if (
                              ["Panel IDs", "User IDs", "Contact ID"].includes(
                                label
                              ) &&
                              (value == null ||
                                value === "" ||
                                (Array.isArray(value) && !value.length))
                            )
                              return null
                            return (
                              <div key={label}>
                                <dt>{label}</dt>
                                <dd>
                                  {Array.isArray(value)
                                    ? value.map(detailText).join(", ")
                                    : detailText(value)}
                                </dd>
                              </div>
                            )
                          })}
                        </dl>
                        <div className="tracker-interview-extra">
                          {Array.isArray(event.panel_members) &&
                          event.panel_members.length ? (
                            <>
                              <h4>Panel Members</h4>
                              {event.panel_members.map((member, memberIndex) => (
                                <div
                                  className="tracker-panel-member"
                                  key={memberIndex}
                                >
                                  <span>{show(member.name)}</span>
                                  <span>{show(member.email)}</span>
                                </div>
                              ))}
                            </>
                          ) : null}
                          {feedback.length > 0 ? (
                            <>
                              <h4>Panel Feedback</h4>
                              {feedback.map(
                              (feedback, feedbackIndex) => (
                                <section
                                  className="tracker-feedback-entry"
                                  key={feedbackIndex}
                                >
                                  <dl>
                                    {[
                                      ["Panel Name", feedback.panel_name],
                                      ["Panel Email", feedback.panel_email],
                                      ["Feedback", feedback.feedback],
                                      ["Rating", feedback.rating],
                                      [
                                        "Recommendation",
                                        feedback.recommendation
                                          ? String(feedback.recommendation)
                                              .replace(/_/g, " ")
                                              .replace(/\b\w/g, letter =>
                                                letter.toUpperCase()
                                              )
                                          : null,
                                      ],
                                      [
                                        "Submitted At",
                                        feedback.submitted_at
                                          ? interviewTime({
                                              event_time: feedback.submitted_at,
                                              time_zone: event.time_zone,
                                            })
                                          : null,
                                      ],
                                    ].map(([label, value]) => (
                                      <div
                                        key={label}
                                        className={
                                          label === "Feedback"
                                            ? "tracker-comment-field"
                                            : undefined
                                        }
                                      >
                                        <dt>{label}</dt>
                                        <dd>{show(value)}</dd>
                                      </div>
                                    ))}
                                  </dl>
                                </section>
                              )
                              )}
                            </>
                          ) : null}
                        </div>
                      </article>
                      )
                    })
                  ) : (
                    <p>No scheduled interview details available</p>
                  )}
                </div>
              ) : detailColumn === 7 ? (
                <div className="tracker-remaining-content">
                  <div className="tracker-remaining-count">
                    <span className="tracker-remaining-icon">
                      <UserPlus size={22} aria-hidden="true" />
                    </span>
                    <div>
                      <span className="tracker-remaining-label">
                        Positions remaining
                      </span>
                      <strong>{detailRow ? summary(detailRow)[7] : 0}</strong>
                    </div>
                  </div>
                  <p className="tracker-remaining-message">
                    {show(detailRow?.yet_to_fill_details?.message)}
                  </p>
                  {Array.isArray(detailRow?.yet_to_fill_details?.candidates) &&
                    detailRow.yet_to_fill_details.candidates.length > 0 && (
                      <div className="tracker-candidate-scroll">
                        {detailRow.yet_to_fill_details.candidates.map(
                          (candidate, index) => (
                            <article key={index}>
                              <CandidateHeading candidate={candidate} />
                              <AddedByDetails candidate={candidate} />
                            </article>
                          )
                        )}
                      </div>
                    )}
                </div>
              ) : (
                <div className="tracker-candidate-scroll">
                  {visibleCandidates.length ? (
                    visibleCandidates.map((candidate, index) => (
                      <article
                        key={`${candidate.candidate_id || index}-${index}`}
                      >
                        <CandidateHeading candidate={candidate} />
                        <AddedByDetails candidate={candidate} />
                        <dl>
                          {candidateFields
                            .filter(
                              ([label]) =>
                                label !== "Joined At" &&
                                (!hideRejectionFields(candidate) ||
                                  !isRejectionField(label))
                            )
                            .map(([label, ...keys]) => (
                              <div
                                key={label}
                                className={
                                  label.includes("Rejection Comments")
                                    ? "tracker-comment-field"
                                    : label === "Acknowledged At"
                                    ? "tracker-time-highlight acknowledged"
                                    : label === "Rejected At"
                                    ? "tracker-time-highlight rejected"
                                    : undefined
                                }
                              >
                                <dt>{label}</dt>
                                <dd>
                                  {renderCandidateValue(
                                    label,
                                    fieldValue(candidate, keys)
                                  )}
                                </dd>
                              </div>
                            ))}
                        </dl>
                        {Array.isArray(candidate.status_history) &&
                          candidate.status_history.length > 0 && (
                            <details className="tracker-status-history">
                              <summary>Read More: HRMS Status Timeline</summary>
                              <ol>
                                {candidate.status_history.map(
                                  (entry, historyIndex) => (
                                    <li key={historyIndex}>
                                      {entry && typeof entry === "object" ? (
                                        <dl>
                                          {Object.entries(entry)
                                            .filter(
                                              ([key]) =>
                                                !isIdField(key) &&
                                                (!hideRejectionFields(entry) ||
                                                  !isRejectionField(key))
                                            )
                                            .map(([key, value]) => {
                                              const normalizedKey = key
                                                .replace(/[^a-z]/gi, "")
                                                .toLowerCase()
                                              const isComment = [
                                                "employeerejectioncomments",
                                                "hrrejectioncomments",
                                              ].includes(normalizedKey)
                                              const label =
                                                normalizedKey ===
                                                "employeerejectioncomments"
                                                  ? "Employee Rejection Comments"
                                                  : normalizedKey ===
                                                    "hrrejectioncomments"
                                                  ? "HR Rejection Comments"
                                                  : key
                                                      .replace(
                                                        /([a-z])([A-Z])/g,
                                                        "$1 $2"
                                                      )
                                                      .replace(/_/g, " ")
                                              return (
                                                <div
                                                  key={key}
                                                  className={
                                                    isComment
                                                      ? "tracker-comment-field"
                                                      : undefined
                                                  }
                                                >
                                                  <dt>{label}</dt>
                                                  <dd>
                                                    {isComment
                                                      ? renderCandidateValue(
                                                          label,
                                                          value
                                                        )
                                                      : detailText(value)}
                                                  </dd>
                                                </div>
                                              )
                                            })}
                                        </dl>
                                      ) : (
                                        detailText(entry)
                                      )}
                                    </li>
                                  )
                                )}
                              </ol>
                            </details>
                          )}
                        {/^(https?:\/\/)/i.test(
                          candidate.resume || candidate.resume_url || ""
                        ) ? (
                          <button
                            type="button"
                            className="tracker-resume-link"
                            onClick={() => {
                              setResumePreviewUrl(
                                candidate.resume || candidate.resume_url
                              )
                              setResumePreviewCandidateKey(
                                `${candidate.candidate_id || index}-${index}`
                              )
                            }}
                          >
                            <Eye size={15} aria-hidden="true" />
                            View Resume
                          </button>
                        ) : (
                          <span>Resume: --</span>
                        )}
                        {resumePreviewUrl &&
                          resumePreviewCandidateKey ===
                            `${candidate.candidate_id || index}-${index}` && (
                            <div className="tracker-resume-preview">
                              <div className="tracker-resume-preview-header">
                                <strong>Resume Preview</strong>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setResumePreviewUrl("")
                                    setResumePreviewCandidateKey("")
                                  }}
                                >
                                  Close preview
                                </button>
                              </div>
                              <iframe
                                title="Resume preview"
                                src={resumePreviewUrl}
                                className="tracker-resume-frame"
                              />
                            </div>
                          )}
                      </article>
                    ))
                  ) : (
                    <p>No candidate details available</p>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
